import React from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import { useForm, Controller } from 'react-hook-form';

import { schoolCreateSchema, schoolUpdateSchema } from '../../schemas';
import { getDistricts } from '../../services/districtsService';
import { SchoolCreateFormData, SchoolUpdateFormData , School } from '../../types';
import { Button } from '../common';
import { Input , PaginatedSelect, PaginatedSelectOption } from '../forms';

interface SchoolFormProps {
  school?: School;
  onSubmit: (data: SchoolCreateFormData | SchoolUpdateFormData) => void;
  onCancel: () => void;
  isLoading?: boolean;
}

export const SchoolForm: React.FC<SchoolFormProps> = ({
  school,
  onSubmit,
  onCancel,
  isLoading = false,
}) => {
  const isEdit = !!school;
  const schema = isEdit ? schoolUpdateSchema : schoolCreateSchema;
  
  const {
    control,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: school
      ? {
          name: school.name,
          code: school.code,
          school_type: school.school_type,
          grade_levels: school.grade_levels,
          district_id: school.district_id,
          principal: school.principal || '',
          email: school.email || '',
          phone: school.phone || '',
          address: school.address || '',
          student_count: school.student_count || undefined,
        }
      : {
          name: '',
          code: '',
          school_type: '',
          grade_levels: '',
          district_id: undefined, // Will be selected from dropdown
          principal: '',
          email: '',
          phone: '',
          address: '',
          student_count: undefined,
        },
  });

  const handleFormSubmit = (
    data: SchoolCreateFormData | SchoolUpdateFormData
  ) => {
    // Convert empty strings to undefined for optional fields
    const cleanedData = Object.fromEntries(
      Object.entries(data).map(([key, value]) => [
        key,
        value === '' ? undefined : value,
      ])
    );
    
    // Ensure district_id is a number
    if (cleanedData['district_id'] !== undefined) {
      cleanedData['district_id'] = Number(cleanedData['district_id']);
    }
    
    onSubmit(cleanedData);
  };

  return (
    <div className="h-full flex flex-col">
      <form
        onSubmit={handleSubmit(handleFormSubmit)}
        className="h-full flex flex-col"
      >
        {/* Scrollable form content */}
        <div className="flex-1 overflow-y-auto p-6">
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* School Name */}
              <div className="col-span-2">
                <Controller
                  name="name"
                  control={control}
                  render={({ field }) => (
                    <Input
                      {...field}
                      value={field.value || ''}
                      label="School Name *"
                      placeholder="Enter school name"
                      error={errors.name?.message}
                    />
                  )}
                />
              </div>

              {/* School Code */}
              <div>
                <Controller
                  name="code"
                  control={control}
                  render={({ field }) => (
                    <Input
                      {...field}
                      value={field.value || ''}
                      label="School Code *"
                      placeholder="e.g., LES"
                      error={errors.code?.message}
                    />
                  )}
                />
              </div>

              {/* School Type */}
              <div>
                <Controller
                  name="school_type"
                  control={control}
                  render={({ field }) => (
                    <Input
                      {...field}
                      value={field.value || ''}
                      label="School Type *"
                      placeholder="e.g., Elementary, Middle, High"
                      error={errors.school_type?.message}
                    />
                  )}
                />
              </div>

              {/* Grade Levels */}
              <div>
                <Controller
                  name="grade_levels"
                  control={control}
                  render={({ field }) => (
                    <Input
                      {...field}
                      value={field.value || ''}
                      label="Grade Levels *"
                      placeholder="e.g., K-5, 6-8, 9-12"
                      error={errors.grade_levels?.message}
                    />
                  )}
                />
              </div>

              {/* District Selection */}
              <div>
                <Controller
                  name="district_id"
                  control={control}
                  render={({ field }) => (
                    <PaginatedSelect
                      label="District *"
                      placeholder="Select a district..."
                      value={field.value || ''}
                      onChange={(value) => field.onChange(Number(value))}
                      error={errors.district_id?.message}
                      multiple={false}
                      disabled={isEdit}
                      loadOptions={async (page: number, search?: string) => {
                        try {
                          const response = await getDistricts({
                            skip: (page - 1) * 10,
                            limit: 10,
                            search: search || undefined
                          });
                          
                          const options: PaginatedSelectOption[] = response.data.map(district => ({
                            label: district.name, // Only show district name, no ID
                            value: district.id
                          }));
                          
                          return {
                            data: options,
                            hasMore: response.pagination.has_next,
                            total: response.pagination.total
                          };
                        } catch (error) {
                          console.error('Error loading districts:', error);
                          return {
                            data: [],
                            hasMore: false,
                            total: 0
                          };
                        }
                      }}
                      searchable={true}
                      searchPlaceholder="Search districts..."
                    />
                  )}
                />
                {/* {isEdit && (
                  <p className="text-sm text-gray-500 mt-1">
                    District cannot be changed after school creation to maintain data integrity.
                  </p>
                )} */}
              </div>

              {/* Principal Name */}
              <div>
                <Controller
                  name="principal"
                  control={control}
                  render={({ field }) => (
                    <Input
                      {...field}
                      value={field.value || ''}
                      label="Principal Name"
                      placeholder="Enter principal name"
                      error={errors.principal?.message}
                    />
                  )}
                />
              </div>

              {/* Contact Email */}
              <div>
                <Controller
                  name="email"
                  control={control}
                  render={({ field }) => (
                    <Input
                      {...field}
                      value={field.value || ''}
                      type="email"
                      label="Contact Email"
                      placeholder="principal@school.edu"
                      error={errors.email?.message}
                    />
                  )}
                />
              </div>

              {/* Contact Phone */}
              <div>
                <Controller
                  name="phone"
                  control={control}
                  render={({ field }) => (
                    <Input
                      {...field}
                      value={field.value || ''}
                      type="tel"
                      label="Contact Phone"
                      placeholder="(555) 123-4567"
                      error={errors.phone?.message}
                    />
                  )}
                />
              </div>

              {/* Student Count */}
              <div>
                <Controller
                  name="student_count"
                  control={control}
                  render={({ field }) => (
                    <Input
                      {...field}
                      value={field.value || ''}
                      type="number"
                      label="Student Count"
                      placeholder="Enter student count"
                      error={errors.student_count?.message}
                    />
                  )}
                />
              </div>

              {/* Address */}
              <div className="md:col-span-2">
                <Controller
                  name="address"
                  control={control}
                  render={({ field }) => (
                    <Input
                      {...field}
                      value={field.value || ''}
                      label="Address"
                      placeholder="Enter full address"
                      error={errors.address?.message}
                    />
                  )}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Form Actions - Sticky at bottom */}
        <div className="flex-shrink-0 flex justify-end space-x-3 p-6 pt-4 border-t border-gray-200 bg-white">
          <Button
            type="button"
            variant="secondary"
            onClick={onCancel}
            disabled={isSubmitting || isLoading}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="primary"
            disabled={isSubmitting || isLoading}
            loading={isSubmitting || isLoading}
          >
            {isEdit ? 'Update School' : 'Create School'}
          </Button>
        </div>
      </form>
    </div>
  );
};
